import { useCallback, useEffect, useState } from "react";
import { Megaphone, Trash2, Pencil, Eye, EyeOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { confirmDialog } from "../ConfirmHost";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const EMPTY = { kind: "changelog", title: "", description: "", date: "", tag: "nieuw", status: "gepland", published: true };

export const UpdatesAdmin = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(() => {
    api.get("/admin/updates").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) return toast.error("Titel is te kort");
    setBusy(true);
    try {
      const payload = {
        kind: form.kind, title: form.title.trim(), description: form.description.trim(), published: form.published,
        date: form.kind === "changelog" ? (form.date || null) : null,
        tag: form.kind === "changelog" ? form.tag : null,
        status: form.kind === "roadmap" ? form.status : null,
      };
      if (editId) {
        await api.patch(`/admin/updates/${editId}`, payload);
        toast.success("Item bijgewerkt");
      } else {
        await api.post("/admin/updates", payload);
        toast.success("Item gepubliceerd op de Wat-is-nieuw pagina");
      }
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const edit = (u) => {
    setEditId(u.id);
    setForm({ kind: u.kind, title: u.title, description: u.description || "", date: u.date || "", tag: u.tag || "nieuw", status: u.status || "gepland", published: u.published });
  };

  const togglePublish = async (u) => {
    await api.patch(`/admin/updates/${u.id}`, { published: !u.published });
    load();
  };

  const remove = async (u) => {
    if (!(await confirmDialog(`"${u.title}" definitief verwijderen?`, { title: "Item verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    await api.delete(`/admin/updates/${u.id}`);
    toast.success("Item verwijderd");
    load();
  };

  return (
    <div data-testid="admin-updates-section" className="mb-10">
      <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4"><Megaphone className="w-5 h-5 text-primary" /> Wat is nieuw & roadmap</h2>
      <form onSubmit={submit} className="bg-card rounded-2xl p-4 space-y-3 mb-5" data-testid="admin-updates-form">
        <div className="flex flex-wrap gap-2">
          <select data-testid="admin-update-kind-select" value={form.kind} onChange={(e) => set({ kind: e.target.value })}
            className="rounded-xl bg-secondary px-3 py-2 text-sm font-semibold outline-none cursor-pointer">
            <option value="changelog">Wat is nieuw</option>
            <option value="roadmap">Roadmap</option>
          </select>
          {form.kind === "changelog" ? (
            <>
              <select data-testid="admin-update-tag-select" value={form.tag} onChange={(e) => set({ tag: e.target.value })}
                className="rounded-xl bg-secondary px-3 py-2 text-sm outline-none cursor-pointer">
                <option value="nieuw">Nieuw</option>
                <option value="verbeterd">Verbeterd</option>
                <option value="opgelost">Opgelost</option>
              </select>
              <Input data-testid="admin-update-date-input" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} className="rounded-xl h-9 w-40" />
            </>
          ) : (
            <select data-testid="admin-update-status-select" value={form.status} onChange={(e) => set({ status: e.target.value })}
              className="rounded-xl bg-secondary px-3 py-2 text-sm outline-none cursor-pointer">
              <option value="gepland">Gepland</option>
              <option value="in_ontwikkeling">In ontwikkeling</option>
              <option value="klaar">Klaar</option>
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs font-semibold ml-auto cursor-pointer">
            <input type="checkbox" data-testid="admin-update-published-checkbox" checked={form.published} onChange={(e) => set({ published: e.target.checked })} className="accent-[#2D6A4F]" />
            Gepubliceerd
          </label>
        </div>
        <Input data-testid="admin-update-title-input" value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={140}
          placeholder="Titel — bv. 'Reacties van familie op je dagverslagen'" className="rounded-xl h-11" />
        <Textarea data-testid="admin-update-description-input" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2} maxLength={2000}
          placeholder="Korte omschrijving (optioneel)" className="rounded-xl" />
        <div className="flex gap-2">
          <Button data-testid="admin-update-submit-button" type="submit" disabled={busy} className="rounded-full bg-primary hover:bg-[#1B4332]">
            <Plus className="w-4 h-4 mr-1.5" /> {editId ? "Bewaar wijzigingen" : "Toevoegen"}
          </Button>
          {editId && (
            <Button type="button" variant="outline" className="rounded-full" onClick={() => { setEditId(null); setForm(EMPTY); }}>Annuleer</Button>
          )}
        </div>
      </form>
      <div className="space-y-2" data-testid="admin-updates-list">
        {items.map((u) => (
          <div key={u.id} data-testid={`admin-update-row-${u.id}`} className={`bg-card rounded-2xl px-4 py-3 flex items-center gap-3 ${!u.published ? "opacity-50" : ""}`}>
            <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 bg-secondary shrink-0">
              {u.kind === "changelog" ? u.tag || "nieuw" : u.status}
            </span>
            {u.kind === "roadmap" && (
              <span data-testid={`admin-update-votes-${u.id}`} className="text-xs font-mono font-bold text-primary shrink-0" title="Stemmen">▲ {u.votes ?? 0}</span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{u.title}</p>
              <p className="text-xs text-muted-foreground font-mono">{u.kind === "changelog" ? u.date : "roadmap"}{!u.published && " · verborgen"}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`admin-update-publish-${u.id}`} onClick={() => togglePublish(u)} title={u.published ? "Verbergen" : "Publiceren"}
                className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
                {u.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button data-testid={`admin-update-edit-${u.id}`} onClick={() => edit(u)} title="Bewerken"
                className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid={`admin-update-delete-${u.id}`} onClick={() => remove(u)} title="Verwijderen"
                className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
